import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { SIDEBAR_ROUTES } from '../../../constants/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { AUTH_API } from '../../../api/amplify';
import { ReactComponent as CopyMonkeyLogo } from '../../../assets/images/logo.svg';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { translateErrorMessage } from '../../../utils/authHelpers';
import { showAlert } from '../../../store';
import { AlertType } from '../../../types/common';
// import { pointer } from '@testing-library/user-event/dist/types/pointer';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down(768)]: {
    width: '100%',
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.down(768)]: {
    width: 0,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface ISidebarProps {
  open: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<ISidebarProps> = ({ onToggle, open }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useAppSelector((state) => state.user);

  const isElamaProvider = user?.provider === 'eLama';

  const handleSignOut = async () => {
    try {
      setIsLoading(true);

      await AUTH_API.signOut();
    } catch (e: any) {
      const errorMessage = translateErrorMessage(e);
      dispatch(showAlert({ type: AlertType.Error, message: errorMessage }));
    }

    setIsLoading(false);
  };

  return (
    <Box>
      <CssBaseline />
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          {open && <CopyMonkeyLogo />}

          <IconButton onClick={onToggle}>
            {open ? <ChevronLeftIcon color={'primary'} /> : <ChevronRightIcon color={'primary'} />}
          </IconButton>
        </DrawerHeader>
        <Divider light />
        <List>
          {SIDEBAR_ROUTES.map(({ name, id, icon, path }) => (
            <ListItem key={id} disablePadding sx={{ display: 'block' }} selected={path === pathname}>
              <ListItemButton
                onClick={() => navigate(path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      path === pathname ? theme.custom.palette.primary.default : theme.custom.palette.primary.basegray,
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {icon}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      variant={path === pathname ? 'h4' : 'h5'}
                      sx={{ color: path === pathname ? theme.custom.palette.primary.default : 'inherit' }}
                    >
                      {name}
                    </Typography>
                  }
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <ListItem disablePadding sx={{ display: 'block', marginTop: 'auto', height: '60px' }}>
          <ListItemText
            primary={<Typography>{isElamaProvider ? 'Службы Заботы eLama' : 'Поддержка:'}</Typography>}
            sx={{ opacity: open ? 1 : 0, color: theme.custom.palette.primary.basegray, marginLeft: '10px' }}
          />
          <ListItemText
            primary={<Typography>{isElamaProvider ? 'milo@elama.ru' : 'hello@copymonkey.ai'}</Typography>}
            sx={{ opacity: open ? 1 : 0, color: theme.custom.palette.primary.default, marginLeft: '10px' }}
          />
        </ListItem>
        <ListItem disablePadding sx={{ display: 'none' }}>
          <ListItemButton
            onClick={handleSignOut}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
            }}
            disabled={isLoading}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} sx={{ color: theme.custom.palette.basegray }} />
              ) : (
                <LogoutRoundedIcon color={'primary'} />
              )}
            </ListItemIcon>
            <ListItemText primary={'Выйти'} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
      </Drawer>
    </Box>
  );
};

export default Sidebar;
